import React, { memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar, Target, Brain, RefreshCw, Shield,
  ArrowRight, CheckCircle, Zap, Sparkles
} from 'lucide-react';
import { haptic } from '../../../shared/utils/haptics';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export const LandingPage = memo(function LandingPage({ onEnterApp }) {
  const handleEnter = useCallback(() => {
    haptic.tap();
    onEnterApp();
  }, [onEnterApp]);

  return (
    <div className="min-h-screen bg-[var(--bg-base,#09090b)] relative overflow-hidden">
      {/* Subtle background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-zinc-800/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] bg-zinc-800/15 rounded-full blur-[100px]" />
      </div>

      <div className="relative max-w-2xl mx-auto px-6 py-16">
        {/* Hero Section */}
        <motion.header
          className="text-center mb-20"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Logo */}
          <motion.div
            className="w-16 h-16 mx-auto glass-card flex items-center justify-center mb-8"
            variants={fadeInUp}
          >
            <Zap className="w-7 h-7 text-zinc-400" />
          </motion.div>

          <motion.h1
            className="text-3xl md:text-4xl font-semibold text-zinc-100 mb-4 tracking-tight"
            variants={fadeInUp}
          >
            My ADHD Tasks
          </motion.h1>

          <motion.p
            className="text-base text-zinc-400 max-w-sm mx-auto mb-10 leading-relaxed"
            variants={fadeInUp}
          >
            Structurez vos journées simplement.
            <br />
            <span className="text-zinc-500">Pensé pour les cerveaux neurodivergents.</span>
          </motion.p>

          <motion.button
            onClick={handleEnter}
            className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-100 hover:bg-white text-zinc-900 rounded-xl font-medium text-sm transition-all"
            variants={fadeInUp}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Commencer
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.header>

        {/* Features Section */}
        <motion.section
          className="mb-20"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <div className="space-y-2">
            <FeatureRow
              icon={<Calendar className="w-5 h-5" />}
              title="Vue journalière"
              description="Visualisez votre journée avec un code couleur clair : vital, corvée, plaisir."
            />
            <FeatureRow
              icon={<Target className="w-5 h-5" />}
              title="Mode Focus"
              description="Concentrez-vous sur une seule tâche avec des sessions minutées."
            />
            <FeatureRow
              icon={<Brain className="w-5 h-5" />}
              title="Gamification"
              description="Gagnez de l'XP et des niveaux pour rester motivé."
            />
            <FeatureRow
              icon={<RefreshCw className="w-5 h-5" />}
              title="Sync Google"
              description="Importez vos événements Google Calendar automatiquement."
            />
          </div>
        </motion.section>

        {/* Trust indicators */}
        <motion.section
          className="mb-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-center gap-6 flex-wrap text-sm text-zinc-500">
            <TrustItem icon={<Shield className="w-4 h-4" />} text="Données locales" />
            <TrustItem icon={<CheckCircle className="w-4 h-4" />} text="RGPD" />
            <TrustItem icon={<Sparkles className="w-4 h-4" />} text="100% gratuit" />
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="p-8 glass-card text-center">
            <h2 className="text-lg font-medium text-zinc-100 mb-2">
              Prêt à simplifier vos journées ?
            </h2>
            <p className="text-zinc-500 text-sm mb-6">
              Aucun compte requis. Commencez immédiatement.
            </p>
            <motion.button
              onClick={handleEnter}
              className="inline-flex items-center gap-2 px-8 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-100 rounded-xl font-medium text-sm transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Accéder à l'application
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </div>
        </motion.section>

        {/* Footer */}
        <motion.footer
          className="flex items-center justify-between text-xs text-zinc-600 pt-8 border-t border-white/[0.06]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <span>My ADHD Tasks Manager</span>
          <div className="flex items-center gap-4">
            <a
              href="https://adhdmanager.cosyandsafe.fr/privacy.html"
              className="hover:text-zinc-400 transition-colors"
            >
              Confidentialité
            </a>
            <a
              href="https://adhdmanager.cosyandsafe.fr/terms.html"
              className="hover:text-zinc-400 transition-colors"
            >
              Conditions
            </a>
          </div>
        </motion.footer>
      </div>
    </div>
  );
});

// Feature row component
const FeatureRow = memo(function FeatureRow({ icon, title, description }) {
  return (
    <motion.div
      className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/[0.02] transition-colors group"
      variants={fadeInUp}
      whileHover={{ x: 4 }}
    >
      <div className="w-10 h-10 rounded-lg glass-card flex items-center justify-center text-zinc-500 flex-shrink-0 group-hover:text-zinc-400 transition-colors">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-medium text-zinc-200 mb-1">{title}</h3>
        <p className="text-sm text-zinc-500 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
});

// Trust indicator component
const TrustItem = memo(function TrustItem({ icon, text }) {
  return (
    <span className="flex items-center gap-1.5 text-zinc-500">
      {icon && <span className="text-zinc-600">{icon}</span>}
      {text}
    </span>
  );
});

export default LandingPage;
